import { UserName } from "@/components/UserName";
import { useBackState } from "@/hooks/useScrollMemory";
import { RecordIntegrityChecks } from "@/components/RecordIntegrityChecks";
import { SyntheticDataBanner } from "@/components/SyntheticDataBanner";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, ExternalLink, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const FIELD_BADGE = {
  oem: { label: "OEM #", cls: "bg-blue-100 text-blue-700" },
  supersession: { label: "Supersession", cls: "bg-violet-100 text-violet-700" },
  value: { label: "Value / basis", cls: "bg-amber-100 text-amber-700" },
  other: { label: "Other", cls: "bg-slate-100 text-slate-600" },
};

export default function ProtectedChangesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [field, setField] = useBackState("field", "all");
  const [from, setFrom] = useBackState("from", "");
  const [to, setTo] = useBackState("to", "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/protected-changes", { params: { field, date_from: from, date_to: to } });
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [field, from, to]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/reports/protected-changes", {
        params: { field, date_from: from, date_to: to, format: "csv" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "protected-changes.csv"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
  };

  return (
    <div data-testid="protected-changes-page">
      <SyntheticDataBanner testid="prot-synthetic-banner" />
      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[#B01E28]" />Protected Changes
        </h1>
        <Button data-testid="prot-export-csv" variant="outline" onClick={exportCsv} disabled={rows.length === 0}
          className="rounded-lg border-slate-300 shrink-0">
          <Download className="w-4 h-4 mr-1.5" />Export CSV
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Every correction to a protected field (OEM #, supersession, value/basis) — who changed what, from → to, and why. Read-only audit.</p>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Field</Label>
          <Select value={field} onValueChange={setField}>
            <SelectTrigger data-testid="prot-field-filter" className="rounded-lg sm:w-48 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-lg">
              <SelectItem value="all">All fields</SelectItem>
              <SelectItem value="oem">OEM number</SelectItem>
              <SelectItem value="supersession">Supersession</SelectItem>
              <SelectItem value="value">Value / basis</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">From</Label>
          <Input data-testid="prot-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-lg mt-1 sm:w-40" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">To</Label>
          <Input data-testid="prot-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-lg mt-1 sm:w-40" />
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center font-mono text-xs text-slate-400">LOADING…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-xl p-12 text-center">
          <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">No protected-field changes in this range.</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="prot-changes-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                <th className="px-3 py-2 font-bold">When</th>
                <th className="px-3 py-2 font-bold">Part</th>
                <th className="px-3 py-2 font-bold">Field</th>
                <th className="px-3 py-2 font-bold">Old → New</th>
                <th className="px-3 py-2 font-bold">Reason</th>
                <th className="px-3 py-2 font-bold">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const fb = FIELD_BADGE[r.field_type] || FIELD_BADGE.other;
                return (
                  <tr key={`${r.tx_number}-${r.field}`} data-testid={`prot-row-${r.tx_number}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap">{(r.timestamp || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2">
                      {r.short_code ? (
                        <Link to={`/parts/${r.part_id}`} className="font-mono text-[#B01E28] hover:underline inline-flex items-center gap-1">{r.short_code}<ExternalLink className="w-3 h-3" /></Link>
                      ) : <span className="font-mono text-slate-400">—</span>}
                      {r.part_name && <span className="block text-[11px] text-slate-500 truncate max-w-[180px]">{r.part_name}</span>}
                    </td>
                    <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-mono ${fb.cls}`}>{fb.label}</span></td>
                    <td className="px-3 py-2 text-[12px]"><span className="font-mono line-through text-slate-400">{r.from}</span> <span className="text-slate-400">→</span> <span className="font-mono font-semibold text-slate-800">{r.to}</span></td>
                    <td className="px-3 py-2 text-[12px] text-slate-600 max-w-[220px]">{r.reason || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-[11px] font-mono text-slate-500 whitespace-nowrap"><UserName email={r.user_email} inline /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <RecordIntegrityChecks />
    </div>
  );
}
